import { supabase } from '../../integrations/supabase/client';
import type { IClientRepository, Client, ClientStats } from '../interfaces/IClientRepository';

export class SupabaseClientRepository implements IClientRepository {
  // Optimized: Batch fetch counts for all clients in parallel with single queries per table
  private async fetchClientCounts(clients: any[]): Promise<Client[]> {
    if (clients.length === 0) return [];
    
    const clientIds = clients.map(c => c.id);
    
    try {
      // Fetch all counts in parallel with single queries per table
      const [brandsData, busUnitsData, oppsData, profilesData] = await Promise.all([
        // Get brand counts grouped by client_id
        (supabase as any)
          .from('brands')
          .select('client_id')
          .in('client_id', clientIds),
        // Get business unit counts
        supabase
          .from('business_units')
          .select('client_id')
          .in('client_id', clientIds),
        // Get opportunity counts
        (supabase as any)
          .from('opportunities')
          .select('client_id')
          .in('client_id', clientIds),
        // Get profile counts
        supabase
          .from('profiles')
          .select('client_id')
          .in('client_id', clientIds),
      ]);

      // Build count maps from results
      const countByClient = (data: any[] | null, field: string = 'client_id') => {
        const counts: Record<string, number> = {};
        (data || []).forEach(row => {
          const id = row[field];
          counts[id] = (counts[id] || 0) + 1;
        });
        return counts;
      };

      const brandCounts = countByClient(brandsData.data);
      const buCounts = countByClient(busUnitsData.data);
      const oppCounts = countByClient(oppsData.data);
      const userCounts = countByClient(profilesData.data);

      // Map counts to clients
      return clients.map(c => ({
        ...c,
        brand_count: brandCounts[c.id] || 0,
        business_unit_count: buCounts[c.id] || 0,
        opportunity_count: oppCounts[c.id] || 0,
        user_count: userCounts[c.id] || 0,
        last_activity: c.updated_at,
      }));
    } catch (err) {
      console.warn('Error fetching client counts:', err);
      // Fallback: return clients with zero counts
      return clients.map(c => ({
        ...c,
        brand_count: 0,
        business_unit_count: 0,
        opportunity_count: 0,
        user_count: 0,
        last_activity: c.updated_at,
      }));
    }
  }

  async getAll(filters?: {
    search?: string;
    status?: string;
    page?: number;
    limit?: number;
    onboardedOnly?: boolean;
  }): Promise<{ clients: Client[]; total: number }> {
    // Check if user is authenticated
    const { data: { session } } = await supabase.auth.getSession();
    if (!session) {
      console.warn('SupabaseClientRepository.getAll: No active session');
      return { clients: [], total: 0 };
    }

    // If onboardedOnly is true, we need to filter by clients that have completed onboarding flows
    // This requires a different query strategy
    if (filters?.onboardedOnly) {
      // First get client IDs that have at least one completed onboarding flow
      const { data: completedFlows, error: flowError } = await supabase
        .from('onboarding_flows')
        .select('client_id')
        .eq('status', 'completed');

      if (flowError) {
        console.error('Error fetching completed flows:', flowError);
        return { clients: [], total: 0 };
      }

      // Get unique client IDs with completed flows
      const completedClientIds = [...new Set((completedFlows || []).map(f => f.client_id).filter(Boolean))];
      
      if (completedClientIds.length === 0) {
        // No clients with completed onboarding flows found
        return { clients: [], total: 0 };
      }

      // Fetch only clients with completed onboarding AND required data populated
      // A truly onboarded client should have at least VAT number OR payment method OR legal type
      let query = supabase
        .from('clients')
        .select('*', { count: 'exact' })
        .in('id', completedClientIds)
        .or('vat_number.not.is.null,payment_method.not.is.null,legal_type.not.is.null,billing_city.not.is.null');

      if (filters?.search) {
        query = query.or(`name.ilike.%${filters.search}%,vat_number.ilike.%${filters.search}%`);
      }

      if (filters?.status) {
        query = query.eq('status', filters.status);
      }

      const page = filters?.page || 1;
      const limit = filters?.limit || 10;
      const start = (page - 1) * limit;
      const end = start + limit - 1;

      const { data, error, count } = await query.range(start, end);

      if (error) {
        console.error('Error fetching onboarded clients:', error);
        return { clients: [], total: 0 };
      }

      // Fetch counts for each client
      const clientsWithCounts = await this.fetchClientCounts(data || []);
      // Counts fetched for onboarded clients
      return { clients: clientsWithCounts, total: count || 0 };
    }

    // Standard query for all clients (used by Onboarding Management)
    let query = supabase
      .from('clients')
      .select('*', { count: 'exact' });

    if (filters?.search) {
      query = query.or(`name.ilike.%${filters.search}%,vat_number.ilike.%${filters.search}%`);
    }

    if (filters?.status) {
      query = query.eq('status', filters.status);
    }

    const page = filters?.page || 1;
    const limit = filters?.limit || 10;
    const start = (page - 1) * limit;
    const end = start + limit - 1;

    const { data, error, count } = await query.range(start, end);

    if (error) {
      console.error('Error fetching clients:', error);
      return { clients: [], total: 0 };
    }

    // Fetch counts for each client using helper method
    const clientsWithCounts = await this.fetchClientCounts(data || []);
    // Counts fetched for clients
    return { clients: clientsWithCounts, total: count || 0 };
  }

  async getById(id: string): Promise<Client | null> {
    const { data, error } = await supabase
      .from('clients')
      .select('*')
      .eq('id', id)
      .single();

    if (error) {
      console.error('Error fetching client by ID:', error);
      throw error;
    }
    if (!data) return null;

    // Get user count separately
    const { count: userCount } = await supabase
      .from('profiles')
      .select('*', { count: 'exact', head: true })
      .eq('client_id', id);

    return {
      ...data,
      status: data.status as 'active' | 'inactive' | 'suspended',
      user_count: userCount || 0,
      last_activity: data.updated_at,
    } as Client;
  }

  async getStats(clientId: string): Promise<ClientStats> {
    const client = await this.getById(clientId);
    if (!client) throw new Error('Client not found');

    return {
      total_users: client.user_count,
      active_users: Math.floor(client.user_count * 0.8),
      pending_invitations: 0,
      onboarding_progress: client.onboarding_completed ? 100 : 50,
      campaigns_active: 0,
    };
  }

  async create(data: { name: string; created_by: string }): Promise<Client> {
    const platformTenantId = localStorage.getItem('platform_tenant_id');
    
    if (!platformTenantId) {
      throw new Error('Platform tenant ID not found');
    }
    
    const { data: client, error } = await supabase
      .from('clients')
      .insert([{
        name: data.name,
        created_by: data.created_by,
        platform_tenant_id: platformTenantId,
        status: 'active',
        onboarding_completed: false,
        admin_unlocked: false,
      }])
      .select()
      .single();

    if (error) throw error;

    return {
      ...client,
      status: 'active',
      user_count: 0,
    } as Client;
  }

  async update(id: string, data: Partial<Client>): Promise<Client> {
    const { data: client, error } = await supabase
      .from('clients')
      .update(data)
      .eq('id', id)
      .select()
      .single();

    if (error) throw error;

    return {
      ...client,
      status: client.status as 'active' | 'inactive' | 'suspended',
      user_count: 0,
    } as Client;
  }

  async suspend(id: string, reason: string): Promise<void> {
    await this.update(id, {
      status: 'suspended',
      suspended_at: new Date().toISOString(),
      suspension_reason: reason,
    } as any);
  }

  async reactivate(id: string): Promise<void> {
    await this.update(id, {
      status: 'active',
      suspended_at: null,
      suspended_by: null,
      suspension_reason: null,
    } as any);
  }
}
